/**
 * Evaluates the `visible_if` expression on a SettingSchema against the
 * current sibling settings (the same block's / section's settings map).
 *
 * Supported syntax — kept tiny on purpose, matches what Shopify-like
 * themes already write in their `{% schema %}` JSON:
 *
 *   "setting_id"                           → truthy
 *   "!setting_id"                          → falsy
 *   "setting_id == 'value'"                → eq to literal
 *   "setting_id != 'value'"                → ne to literal
 *   "{{ block.settings.id }}"              → same as "id"
 *   "{{ section.settings.id }}"            → same as "id"
 *   "{{ block.settings.id == 'value' }}"   → eq to literal
 *   "a && b"  / "a && b == 'x'"            → logical AND of simple clauses
 *   "a || b"                               → logical OR of simple clauses
 *
 * `&&` binds tighter than `||`, like in JS. Parens and mixed mixing
 * aren't supported — if you need that, split into multiple settings.
 *
 * Unknown or malformed expressions default to VISIBLE (fail open) so a
 * typo in the theme doesn't make a whole panel disappear.
 */

const LIQUID_RE = /^\s*\{\{\s*(.+?)\s*\}\}\s*$/;

/** Evaluate a single simple clause (no && or ||). */
function evalClause(clause: string, values: Record<string, any>): boolean {
  const body = clause.trim();

  // Comparison forms: `x == 'value'`, `x != 'value'`
  const cmp = body.match(
    /^(!?)\s*([A-Za-z_][A-Za-z0-9_]*)\s*(==|!=)\s*('([^']*)'|"([^"]*)"|true|false|[\d.]+)\s*$/
  );
  if (cmp) {
    const negated = cmp[1] === '!';
    const id = cmp[2];
    const op = cmp[3];
    let literal: any = cmp[5] ?? cmp[6];
    if (literal === undefined) {
      if (cmp[4] === 'true') literal = true;
      else if (cmp[4] === 'false') literal = false;
      else literal = Number(cmp[4]);
    }
    const actual = values[id];
    const eq =
      typeof literal === 'boolean'
        ? !!actual === literal
        : literal === '' || literal == null
          ? actual == null || actual === ''
          : String(actual) === String(literal);
    const result = op === '==' ? eq : !eq;
    return negated ? !result : result;
  }

  // Bare truthy / negated forms
  const truthy = body.match(/^(!?)\s*([A-Za-z_][A-Za-z0-9_]*)\s*$/);
  if (truthy) {
    const negated = truthy[1] === '!';
    const v = values[truthy[2]];
    return negated ? !v : !!v;
  }

  // Unknown form → fail open so a theme typo doesn't disappear UI.
  return true;
}

export function isVisible(
  expr: string | undefined,
  values: Record<string, any>
): boolean {
  if (!expr) return true;

  // Strip Liquid output braces if present.
  const m = expr.match(LIQUID_RE);
  let body = m ? m[1] : expr;
  body = body.trim();

  // Drop the "block.settings." / "section.settings." prefixes so the rest
  // of the parser only deals with bare setting ids.
  body = body
    .replace(/\bblock\.settings\./g, '')
    .replace(/\bsection\.settings\./g, '');

  // Split on || first (lowest precedence), then each OR-term is split on &&.
  // `a && b || c && d` → ((a && b) || (c && d)). Matches JS semantics.
  // We don't support parens to keep the parser honest — themes that need
  // more structure should split the setting instead.
  const orParts = body.split(/\s*\|\|\s*/);
  for (const orPart of orParts) {
    const andParts = orPart.split(/\s*&&\s*/);
    const allTrue = andParts.every((c) => evalClause(c, values));
    if (allTrue) return true;
  }
  return false;
}
